import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useNavigate, useLocation } from 'react-router-dom'
import { useLoginModal } from '@/contexts/LoginModalContext'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/supabase/client'
import type { ReferralEvent } from '@/supabase/referral/repository/referralEventRepository'
import { formatEventLastDay } from '@/lib/referralEvent'

// 이벤트마다 새 키 — 1월 이벤트 때 '7일간 보지 않기' 이력과 분리
const STORAGE_KEY = 'invite_announcement_2026_yearend_hidden_until'
const DAYS_TO_HIDE = 7

function getHiddenUntil(): Date | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    return value ? new Date(value) : null
  } catch {
    return null
  }
}

function setHiddenUntil(date: Date) {
  try {
    localStorage.setItem(STORAGE_KEY, date.toISOString())
  } catch {
    // 저장 실패는 무시 — 이번 세션의 노출 억제는 isOpen state가 담당.
  }
}

// 열린 이벤트(LaunchNoticeGate가 판정)를 받아 공지. 종료 시각 경과는 열기 직전 다시 확인한다.
export default function InviteAnnouncementModal({ event }: { event: ReferralEvent }) {
  const [isOpen, setIsOpen] = useState(false)
  const [isRefereeBeforeFirstPayment, setIsRefereeBeforeFirstPayment] = useState<boolean | null>(null)
  const navigate = useNavigate()
  const location = useLocation()
  const { showSignupForm } = useLoginModal()
  const { user } = useAuth()

  // 피추천인이면서 첫 결제 전인지 체크
  useEffect(() => {
    const checkRefereeStatus = async () => {
      if (!user) {
        setIsRefereeBeforeFirstPayment(false)
        return
      }

      const { data, error } = await supabase
        .from('referral')
        .select('first_payment_at')
        .eq('referee_id', user.id)
        .single()

      // 피추천인이 아니거나 조회 실패 시 false
      if (error || !data) {
        setIsRefereeBeforeFirstPayment(false)
        return
      }

      // 피추천인이고 첫 결제 전이면 true
      setIsRefereeBeforeFirstPayment(data.first_payment_at === null)
    }

    checkRefereeStatus()
  }, [user])

  // 페이지 이동할 때마다 체크
  useEffect(() => {
    // 아직 피추천인 여부 확인 중이면 대기
    if (isRefereeBeforeFirstPayment === null) {
      return
    }

    // 피추천인이고 첫 결제 전이면 모달 숨김
    if (isRefereeBeforeFirstPayment) {
      setIsOpen(false)
      return
    }

    // 회원가입 폼이 떠있으면 모달 숨김
    if (showSignupForm) {
      setIsOpen(false)
      return
    }
    
    
    // 초대장 페이지 위에는 띄우지 않음 (페이지 자체가 같은 안내)
    if (location.pathname === '/invite') {
      setIsOpen(false)
      return
    }

    // 이벤트 종료 시각이 지났으면 열지 않음 (오래 열린 세션)
    if (Date.parse(event.end_at) <= Date.now()) {
      setIsOpen(false)
      return
    }

    const hiddenUntil = getHiddenUntil()
    if (hiddenUntil && new Date() < hiddenUntil) {
      // 아직 숨김 기간이 남아있음
      setIsOpen(false)
      return
    }
    
    // 팝업 표시
    setIsOpen(true)
  }, [location.pathname, showSignupForm, isRefereeBeforeFirstPayment, event.end_at])

  // 닫기 = 다음날 0시까지 숨김 (페이지 이동마다 다시 뜨지 않게)
  const handleClose = () => {
    const tomorrow = new Date()
    tomorrow.setHours(24, 0, 0, 0)
    setHiddenUntil(tomorrow)
    setIsOpen(false)
  }

  const handleHideFor7Days = () => {
    const futureDate = new Date()
    futureDate.setDate(futureDate.getDate() + DAYS_TO_HIDE)
    setHiddenUntil(futureDate)
    setIsOpen(false)
  }

  const handleGoToInvite = () => {
    handleClose()
    navigate('/invite')
  }

  if (!isOpen) return null

  const m = event.reward_multiplier

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50">
      <div className="relative w-full max-w-md max-h-[90vh] overflow-y-auto bg-white rounded-xl shadow-xl z-[10000]">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-1 rounded hover:bg-gray-100 transition-colors"
          aria-label="닫기"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>

        <div className="px-6 pt-6 pb-5 text-sm leading-relaxed text-gray-700">
          <h2 className="pr-8 text-lg font-bold text-gray-900">🎁 연말 초대 이벤트</h2>
          <p className="mt-1 text-xs text-gray-400">셀러헬퍼</p>

          <p className="mt-5">안녕하세요, 셀러헬퍼입니다.</p>
          <p className="mt-3">
            {formatEventLastDay(event.end_at)}까지 초대 보상을 <strong className="text-gray-900">{m}배</strong>로 드립니다.
          </p>

          <p className="mt-5 font-semibold text-gray-900">💡 혜택 안내</p>
          <ul className="mt-2 ml-5 list-disc space-y-3">
            <li>
              <span className="font-medium text-gray-900">초대받은 분</span> (첫 결제 후 승인 시)
              <ul className="mt-1 ml-5 list-[circle]">
                <li>
                  구독 기간 <s className="text-gray-400">1개월</s> → <strong className="text-red-500">{m}개월</strong> 추가
                </li>
              </ul>
            </li>
            <li>
              <span className="font-medium text-gray-900">초대한 분</span> (구독 중일 때)
              <ul className="mt-1 ml-5 list-[circle] space-y-1">
                <li>
                  1명당 <s className="text-gray-400">1개월</s> → <strong className="text-red-500">{m}개월</strong> 추가
                </li>
                <li>
                  5번째 승인은 <s className="text-gray-400">2개월</s> → <strong className="text-red-500">{m * 2}개월</strong> 추가
                </li>
              </ul>
            </li>
          </ul>

          <p className="mt-4 ml-5">
            ⏳ <span className="font-medium text-gray-900">이벤트 마감:</span> {formatEventLastDay(event.end_at)}까지
            {event.max_referees && <span className="text-gray-500"> (결제 순서 기준 선착순 {event.max_referees}명)</span>}
          </p>

          <p className="mt-5 text-xs text-gray-400">본인 중복 가입 등 부정 참여가 확인되면 보상이 취소될 수 있습니다.</p>
          <p className="mt-3">항상 신뢰해주셔서 감사합니다.</p>
        </div>

        <div className="px-6 pb-6">
          <Button onClick={handleGoToInvite} className="w-full bg-gray-900 text-white hover:bg-gray-800">
            초대장 확인하기
          </Button>
          <div className="mt-2 flex justify-between text-xs text-gray-500">
            <button onClick={handleHideFor7Days} className="rounded px-1 py-2 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400">
              7일간 보지 않기
            </button>
            <button onClick={handleClose} className="rounded px-1 py-2 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400">
              닫기
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
